import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Chip, Tab } from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";

import agent from "../../utils/axios";
import { hasAnyRole } from "../../utils/auth";
import { PageHeader } from "../../components/ui";
import PendingBills from "./PendingBills";
import AllBills from "./AllBills";

/** Count pill next to a tab label: brand when the tab is active, neutral otherwise. */
function TabCount({ value, active }: { value: number; active: boolean }) {
  return (
    <Chip
      component="span"
      size="small"
      label={value.toLocaleString("en-US")}
      color={active ? "primary" : "default"}
      sx={{
        height: 20,
        fontSize: "0.6875rem",
        fontVariantNumeric: "tabular-nums",
        cursor: "inherit",
        "& .MuiChip-label": { px: 1.75 },
      }}
    />
  );
}

function TabLabel({
  label,
  count,
  active,
}: {
  label: string;
  count: number | null;
  active: boolean;
}) {
  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 2 }}
    >
      {label}
      {count !== null && <TabCount value={count} active={active} />}
    </Box>
  );
}

const BillList = () => {
  const canViewPendingBills = hasAnyRole(["Admin", "Developer"]);
  const [value, setValue] = React.useState(canViewPendingBills ? "1" : "2");
  // Needs attention count: fetched on mount (one row is enough, the API returns the total).
  const [attentionCount, setAttentionCount] = useState<number | null>(null);
  // All bills count: only known once that tab has loaded (it is not fetched on mount).
  const [allBillsCount, setAllBillsCount] = useState<number | null>(null);
  const attentionRequest = useRef(0);

  const loadAttentionCount = useCallback(async () => {
    const requestId = ++attentionRequest.current;
    try {
      const response = await agent.Bills.getAllFailedBills(
        0,
        1,
        "billStatus",
        "asc",
        "",
        "attention"
      );
      if (requestId !== attentionRequest.current) return;
      const count = Number(response?.count);
      setAttentionCount(Number.isFinite(count) ? count : null);
    } catch (error) {
      // The tab still works without its badge; Needs attention shows its own error.
      if (requestId === attentionRequest.current) setAttentionCount(null);
    }
  }, []);

  useEffect(() => {
    if (canViewPendingBills) loadAttentionCount();
  }, [canViewPendingBills, loadAttentionCount]);

  // A Needs attention load already carries the same total; take it so the badge stays fresh.
  const handleAttentionCount = useCallback((count: number) => {
    attentionRequest.current++;
    setAttentionCount(count);
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  return (
    <React.Fragment>
      <PageHeader
        title="Bills"
        subtitle="Every bill CSI Pay received from COMPASS, and the ones that need someone to act."
        breadcrumbs={[{ label: "Payments" }, { label: "Bills" }]}
      />

      <Box sx={{ width: "100%" }}>
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={handleChange} aria-label="Bills tabs">
              {canViewPendingBills && (
                <Tab
                  value="1"
                  label={
                    <TabLabel
                      label="Needs attention"
                      count={attentionCount}
                      active={value === "1"}
                    />
                  }
                />
              )}
              <Tab
                value="2"
                label={
                  <TabLabel
                    label="All bills"
                    count={allBillsCount}
                    active={value === "2"}
                  />
                }
              />
            </TabList>
          </Box>
          {canViewPendingBills && (
            <TabPanel value="1" sx={{ px: 0, pt: 5, pb: 0 }}>
              <PendingBills
                attentionCount={attentionCount}
                onAttentionCount={handleAttentionCount}
                onConverted={loadAttentionCount}
              />
            </TabPanel>
          )}
          <TabPanel value="2" sx={{ px: 0, pt: 5, pb: 0 }}>
            <AllBills onCount={setAllBillsCount} />
          </TabPanel>
        </TabContext>
      </Box>
    </React.Fragment>
  );
};

export default BillList;
